import 'arrayq';

import { PureConnect, PureFunctional, React } from '../../react';
import { FontIcon } from 'react-toolbox/lib/font_icon/FontIcon';
import { ICategory, INote } from '../../types';
import { categoriesEditor, categoriesResource } from '../categories/resource';
import Label from '../categoryLabel';

import { selectors as metaSelectors } from '../../meta/selectors';
import reselectors from './reselectors';
import selectors from './selectors';

import CANCEL from './actions/CANCEL';
import SELECT from './actions/SELECT';
import SHOW from './actions/SHOW';
import windowSelectors from '../../window/selectors';

const countMostUsed = 3;

export type SelectMode = 'filter' | 'edit';

function handleEnters(evt: React.KeyboardEvent<HTMLDivElement>, funct: ()=>void) {
    if (evt.key==='Enter') {evt.preventDefault(); funct();}
}   

interface ICategoryPopupSelectionProps {
    mode: SelectMode;
}

interface ICategoryDropdownProps {
    label: string;
    categoryId: number;
    mode: SelectMode;
    onChange: (iCategory: ICategory) => any;
    onAbort: () => void;
}

interface ICategoryProps {
    categoryId: number;
}

export const CategoryPopupSelection = PureConnect<any | ICategoryPopupSelectionProps>(`CategoryPopupSelection`)(
    (state) => ({
        categories: categoriesResource.selectors.items(state),
        currentitem: notesEditor.selectors.item(state),
        visible: selectors.visible(state),
        status: state,
    }),
    (dispatch) => ({
        open: () => dispatch(SHOW({ })),
        change: (item: INote | any) => {dispatch(notesEditor.actions.APPLY({ item }));},
    }),
    ({ mode, currentitem, change }) => {
        return (      
            <CategorySelectModel onSelect={(c) => {
                currentitem.categoryId = c.id;
                change({ currentitem });}} mode={mode} />
        );
    });

export default PureConnect<any|ICategoryDropdownProps>(`CategoryDropdown`)(
    (state) => ({
        categories: categoriesResource.selectors.items(state),
        visible: selectors.visible(state),
    }),
    (dispatch) => ({
        open: () => dispatch(SHOW({ })),
    }),
    ({ visible, categoryId, mode, categories, open, onChange }) => {
        const multiple: ICategory[] = mode === 'edit' && categoryId === undefined
            ? [{ id: undefined, name: 'Meerdere categorieën', color: '#7f7f7f' }]
            : mode === 'filter'
                ? [{ id: undefined, name: 'Alle categorieën', color: '#efefef' }]
                : [];
        const categoriesWithEmpty = [
            ...multiple,
            { id: 0, name: 'Geen categorie', color: '#efefef' },
            ...categories,
        ];

        const item = categoriesWithEmpty.qWhere((x) => x.id === categoryId).qFirst();

        return (
            <span tabIndex={-1} className={cx('wrapped')}>
                <span tabIndex={0} 
                    role="button"
                    aria-labelledby={'id-label-button'} 
                    onKeyDown={(ev) => {if (!visible&&ev.key==='Enter') return open();}} 
                    className={cx('component')} 
                    onClick={() => {if (!visible) return open();}}>

                    <Label id={'id-label-button'} 
                        color={item && item.color} 
                        name={item && item.name} />
                    <div aria-hidden="true" className={cx('arrow')}><FontIcon value="arrow_drop_down" /></div>
                    <CategorySelectModel onSelect={onChange} mode={mode} />
                </span>
             </span> 
        );
});

export const Category = PureConnect<any |ICategoryProps>(`Category`)(
    (state) => ({
        categories: categoriesResource.selectors.items(state),
    }),
    (dispatch) => ({
        open: () => dispatch(SHOW({ })),
    }),
    ({  categoryId, categories, extraClassNames }) => {
  
        const categoriesWithEmpty = [
            { id: 0, name: 'Geen categorie', color: '#efefef' },
            ...categories,
        ];

        const item = categoriesWithEmpty.qWhere((x) => x.id === categoryId).qFirst();

        return (
            <span tabIndex={-1} className={cx('wrapped','selected-category', extraClassNames)}>
                    <Label id={'id-label-button'} 
                        color={item && item.color} 
                        name={item && item.name} />
            </span> 
        );
});

import cx from './style.less';
import { Btn } from '../../react-toolbox-redesign/button';
import { notesEditor } from '../notes/resource';
export const CategoryList = PureFunctional<{ categories: ICategory[]; onClick: (category: ICategory) => void; }>(`CategoryList`)(
    ({ categories, onClick }) => {
        return (
            <div className={cx('list')}>
                {categories.qSelect((item, idx) => 
                    (<div tabIndex={0} 
                        role="button"
                        aria-label={`Categorie ` + item.name}
                        key={idx} 
                        className={cx('item')} 
                        onClick={() => onClick(item)}
                        onKeyDown={(ev) => { handleEnters(ev, ()=> onClick(item)); }}>
                        <Label color={item.color} name={item.name} big />
                    </div>))}
            </div>
        );
    });

export const CategorySelectModel = PureConnect<{ mode: SelectMode, onSelect: (category: ICategory) => void; }>(`CategorySelectModel`)(
    (state) => ({
        categories: categoriesResource.selectors.items(state),
        categoryId: selectors.categoryId(state),
        documentId: metaSelectors.docId(state),
        mostUsed: reselectors.mostUsed(state),
        visible: selectors.visible(state),
        categorycreating: categoriesEditor.selectors.creating(state),
        categoryupdating: categoriesEditor.selectors.updating(state),
        screenSmall: windowSelectors.small(state),
    }),
    (dispatch, { onSelect }) => ({
        cancel() { dispatch(CANCEL({ })); },
        select(category: ICategory) { onSelect(category); dispatch(SELECT({ categoryId: category.id })); },
    }),
    ({ visible, categorycreating, categoryupdating, categoryId, mode, categories, cancel, select, mostUsed }) => {
        const committed = categories.qWhere((x) => categoriesResource.fields.hasCommited(x));
        const mostUsedList = mostUsed.qSelect((x) => committed.qFirst((y) => y.id === x.id)).qWhere(Boolean).slice(0, countMostUsed);
        const multiple: ICategory[] = mode === 'edit' && categoryId === undefined
            ? [{ id: undefined, name: 'Meerdere categorieën', color: '#7f7f7f' }]
            : mode === 'filter'
                ? [{ id: undefined, name: 'Alle categorieën', color: '#efefef' }]
                : [];
        const categoriesWithEmpty = [
            ...multiple,
            { id: 0, name: 'Geen categorie', color: '#efefef' },
            ...committed.qWhere((x) => !mostUsedList.qIncludes(x)).sort((a, b) => a.name.localeCompare(b.name)),
        ];
        return (
                 <div className={cx( !(visible) || categorycreating || categoryupdating? 'wcag-invisible' : '')} aria-hidden={!(visible)}> 
                    <section role="contentinfo" aria-label={'Sluit sectie'} className={cx('content-container')}>
                        <div className={cx('button-container')}>
                            <Btn 
                                type="button" 
                                className={cx('btn-icon-text_back', 'icon_arrow-back')} 
                                aria-label={'Terug gaan'}
                                aria-haspopup={true}                        
                                tabIndex={0}                                                                                                                        
                                onClick={() => cancel()}
                                disabled={false}>
                                    Terug
                            </Btn>
                        </div>                           
                        <div className={cx('sub-title')}>Selecteer een categorie</div>
                    </section>
                    <section role="contentinfo" aria-label={'Lijst met te selecteren categorie'} className={cx('content-container', 'scroll')}>
                        
                        <section role="contentinfo" aria-label={'Laatst gebruikt'}>
                            <div className={cx('section-title')}>
                                Laatst gebruikt
                            </div>
                            <CategoryList categories={mostUsedList} onClick={select} />
                        </section>
                        <section role="contentinfo" aria-label={'Alle categorieën'}>
                            <div className={cx('section-title')}>Alle categorieën</div>
                            <CategoryList categories={categoriesWithEmpty} onClick={select} />
                        </section> 
                    </section>        
             </div>

        );
    });
    
export const CategorySelectModelSaveArea = PureConnect<{}>(`CategorySelectModel`)(
    (state) => ({
        categories: categoriesResource.selectors.items(state),
        categoryId: selectors.categoryId(state),
        documentId: metaSelectors.docId(state),
        visible: selectors.visible(state),
        categorycreating: categoriesEditor.selectors.creating(state),
        categoryupdating: categoriesEditor.selectors.updating(state),        
    }),
    (dispatch) => ({
        createCategory(documentId: string) { dispatch(categoriesEditor.actions.CREATE({ item: { documentId } })); },
    }),
    ({ createCategory, visible, categorycreating, categoryupdating,documentId }) => {

        return (
            <div className={cx('bottom-container', !(visible) || categorycreating || categoryupdating? 'wcag-invisible' : '')} aria-hidden={!(visible) || categorycreating || categoryupdating}> 
                <div className={cx('button-container')} role="contentinfo" aria-label={'Sluit sectie'}>
                    <Btn 
                        type="button" 
                        className={cx('btn','secondary-button_icon')} 
                        aria-label={'Nieuwe categorie aanmaken'}
                        aria-haspopup={true}                        
                        tabIndex={0}                                                                                                                        
                        onClick={() => createCategory(documentId)}
                        disabled={false}>
                            <span className={cx('icon-plus')}>NIEUWE CATEGORIE</span>
                    </Btn> 
                </div>
            </div>
        );
    });



// WEBPACK FOOTER //
// (webpack)-remove-block-loader??ref--5-0!./src/note/categorySelectPopup/input.tsx